/**
 * Demo User Configuration
 * 
 * This file controls what features are visible/accessible for demo users.
 * Used for Google verification and limited access testing.
 */

// Features that demo users CAN access
export const DEMO_ALLOWED_FEATURES = {
  // Core features for demonstration
  main: true,              // Main page/home
  profile: true,           // View profile
  
  // Limited app access
  tourManager: false,      // Tour Manager (HIDDEN)
  contacts: false,         // Contacts app (HIDDEN)
  calendar: true,          // Calendar view
  tasks: true,             // Tasks app
  analytics: false,        // Analytics (HIDDEN)
  
  // Content features (read-only)
  catalog: false,          // Music catalog (HIDDEN)
  bookings: false,         // Bookings (HIDDEN)
  merch: false,            // Merchandise
  
  // Social/public features
  vault: true,             // Public vault view
  
  // Team features (hidden)
  teamManagement: false,   // Team management (HIDDEN)
  delegateAccess: false,   // Delegate access
  
  // Settings (limited)
  settings: false,         // Settings (view only)
  subscription: false,     // No subscription access
  advancedSettings: false, // No advanced settings
  
  // Creation features (disabled)
  createContent: false,    // Can't create new content
  editContent: false,      // Can't edit content
  deleteContent: false,    // Can't delete content
  
  // AI features
  aiManager: true,         // AI chat limited
  
  // Financial features (hidden)
  financial: false,        // Financial data
  
  // Apps
  apps: true,              // Apps page
  connectors: true,        // Connectors
  workflows: false,        // Workflows (HIDDEN)
  notes: true,             // Notes
  knowledge: true,         // Knowledge base
  
  // Other
  more: true,              // More menu (limited)
  search: true,            // Search functionality
  notifications: false,    // Notifications
};

// Routes that demo users can access (whitelist approach)
export const DEMO_ALLOWED_ROUTES = [
  '/',
  '/main',
  '/vault',
  '/vault/*',
  '/calendar',
  '/more',
  '/notes',
  '/notes/*',
  '/connectors',
  '/connectors/*',
  '/tasks',
  '/tasks/*',
  '/knowledge-base',
  '/knowledge-base/*',
  '/apps',
  '/manager-dashboard',
  '/manager-dashboard/*',
];

// Routes that should redirect demo users to home
export const DEMO_BLOCKED_ROUTES = [
  '/settings',
  '/settings/*',
  '/team-management',
  '/team-management/*',
  '/delegate-access',
  '/subscription',
  '/contacts',
  '/contacts/*',
  '/catalog',
  '/catalog/*',
  '/bookings',
  '/bookings/*',
  '/tour-manager',
  '/tour-manager/*',
  '/analytics',
  '/analytics/*',
  '/merch',
  '/merch/*',
  '/unreleased',
  '/unreleased/*',
  '/pages',
  '/pages/*',
  '/flows',
  '/flows/*',
];

// Demo user credentials
export const DEMO_USER_DATA = {
  email: 'demo@dizee.app',
  username: 'Demo User',
  professionRole: 'demo',
  isDemo: true,
  restrictions: {
    canCreate: false,
    canEdit: false,
    canDelete: false,
    canExport: false,
    canInvite: false,
    canAccessFinancials: false,
    canManageTeam: false,
  }
};

/**
 * Check if a demo user can access a specific feature
 * @param {string} featureName - Name of the feature to check
 * @returns {boolean} - Whether the feature is accessible
 */
export const isDemoFeatureAllowed = (featureName) => {
  return DEMO_ALLOWED_FEATURES[featureName] === true;
};

/**
 * Check if a demo user can access a specific route
 * @param {string} pathname - Current route pathname
 * @returns {boolean} - Whether the route is accessible
 */
export const isDemoRouteAllowed = (pathname) => {
  console.log('🔍 Checking route access for demo user:', pathname);
  
  // Check if route is explicitly blocked
  const isBlocked = DEMO_BLOCKED_ROUTES.some(blockedRoute => {
    if (blockedRoute.endsWith('/*')) {
      return pathname.startsWith(blockedRoute.replace('/*', ''));
    }
    return pathname === blockedRoute;
  });
  
  if (isBlocked) {
    console.log('❌ Route is blocked:', pathname);
    return false;
  }
  
  // If not blocked, allow it (permissive approach)
  console.log('✅ Route is allowed:', pathname);
  return true;
};

/**
 * Check if the current user is a demo user
 * @returns {boolean} - Whether current user is demo user
 */
export const isCurrentUserDemo = () => {
  try {
    const user = JSON.parse(localStorage.getItem('dizeeUser'));
    return user?.isDemo === true || user?.professionRole === 'demo';
  } catch {
    return false;
  }
};

/**
 * Get demo mode status message for UI
 * @returns {string} - Demo mode status message
 */
export const getDemoModeMessage = () => {
  return 'Demo Mode - Limited features available for preview';
};

/**
 * Action restrictions for demo users
 */
export const DEMO_ACTION_MESSAGES = {
  create: 'Creating content is not available in demo mode',
  edit: 'Editing content is not available in demo mode',
  delete: 'Deleting content is not available in demo mode',
  export: 'Exporting data is not available in demo mode',
  invite: 'Inviting team members is not available in demo mode',
  financial: 'Financial data is not available in demo mode',
  settings: 'Changing settings is not available in demo mode',
};

/**
 * Get restriction message for an action
 * @param {string} action - Action type
 * @returns {string} - Restriction message
 */
export const getDemoRestrictionMessage = (action) => {
  return DEMO_ACTION_MESSAGES[action] || 'This feature is not available in demo mode';
};

